import { forwardRef, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/utils';
import { Tooltip } from './Tooltip';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  /** Eye toggle for password fields (icon + tooltip only). */
  passwordToggle?: boolean;
}

/**
 * IUI Input Component
 * 
 * Minimal input field with subtle borders and smooth focus states.
 * Follows IUI design principles.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      error,
      hint,
      leftIcon,
      rightIcon,
      passwordToggle = false,
      id,
      type,
      ...props
    },
    ref
  ) => {
    const inputId = id || props.name;
    const [passwordVisible, setPasswordVisible] = useState(false);
    const resolvedType = passwordToggle ? (passwordVisible ? 'text' : 'password') : type;

    const passwordToggleButton = passwordToggle ? (
      <Tooltip content={passwordVisible ? 'Hide password' : 'Show password'} position="left" delay={100}>
        <button
          type="button"
          onClick={() => setPasswordVisible((prev) => !prev)}
          className="flex h-8 w-8 items-center justify-center rounded-md text-khiimori-text-muted hover:text-khiimori-text-primary hover:bg-khiimori-bg-elevated/50 transition-colors"
          aria-label={passwordVisible ? 'Hide password' : 'Show password'}
          tabIndex={-1}
        >
          {passwordVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </Tooltip>
    ) : null;

    const trailing = passwordToggleButton ?? rightIcon;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-khiimori-text-primary mb-1.5"
          >
            {label}
          </label>
        )}
        
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-khiimori-text-muted">
              {leftIcon}
            </div>
          )}
          
          <input
            ref={ref}
            id={inputId}
            type={resolvedType}
            className={cn(
              // Base styles
              'w-full h-10 px-3 py-2',
              'bg-khiimori-bg-elevated border border-khiimori-border rounded-md',
              'text-khiimori-text-primary placeholder:text-khiimori-text-muted',
              'transition-all duration-150 ease-out',
              // Focus state
              'focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500',
              // Error state
              error && 'border-error-500 focus:ring-error-500/30 focus:border-error-500',
              // Disabled state
              props.disabled && 'bg-khiimori-bg-tertiary cursor-not-allowed opacity-60',
              // Hide native browser password reveal (avoids duplicate with our toggle)
              passwordToggle && '[&::-ms-reveal]:hidden [&::-ms-clear]:hidden',
              // Icon padding
              leftIcon && 'pl-10',
              trailing && 'pr-10',
              className
            )}
            {...props}
          />
          
          {trailing && (
            <div className="absolute right-1 top-1/2 -translate-y-1/2 text-khiimori-text-muted">
              {trailing}
            </div>
          )}
        </div>
        
        {error && (
          <p className="mt-1.5 text-sm text-error-500 animate-fade-in">
            {error}
          </p>
        )}
        
        {hint && !error && (
          <p className="mt-1.5 text-sm text-khiimori-text-muted">
            {hint}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

// Textarea variant
export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, error, hint, id, ...props }, ref) => {
    const inputId = id || props.name;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-khiimori-text-primary mb-1.5"
          >
            {label}
          </label>
        )}
        
        <textarea
          ref={ref}
          id={inputId}
          className={cn(
            'w-full min-h-[100px] px-3 py-2',
            'bg-khiimori-bg-elevated border border-khiimori-border rounded-md',
            'text-khiimori-text-primary placeholder:text-khiimori-text-muted',
            'transition-all duration-150 ease-out',
            'focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500',
            error && 'border-error-500 focus:ring-error-500/30 focus:border-error-500',
            props.disabled && 'bg-khiimori-bg-tertiary cursor-not-allowed opacity-60',
            className
          )}
          {...props}
        />
        
        {error && (
          <p className="mt-1.5 text-sm text-error-500 animate-fade-in">
            {error}
          </p>
        )}
        
        {hint && !error && (
          <p className="mt-1.5 text-sm text-khiimori-text-muted">
            {hint}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';